import { toPng } from "html-to-image";
import type { CardConfig, DataValues } from "../types/narrative";

export interface CardData {
  cardConfig: CardConfig;
  sectionId: string;
  sectionLabel?: string;
  sectionIcon?: string;
  reaction?: string | null;
  customerName: string;
  date: string;
  data: DataValues;
}

/**
 * Generate a sanitized filename for card export
 * Format: {customer}-{section}-{date}.png
 */
export function generateCardFilename(
  customerName: string,
  sectionLabel: string,
  date: string,
): string {
  // Sanitize customer name: lowercase, replace spaces/special chars with hyphens
  const sanitizedCustomer = customerName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, ""); // Remove leading/trailing hyphens

  // Sanitize section label
  const sanitizedSection = sectionLabel
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  // Format date (assuming ISO or similar format, extract year-month)
  const dateObj = new Date(date);
  const formattedDate = isNaN(dateObj.getTime())
    ? "export"
    : `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, "0")}`;

  return `${sanitizedCustomer}_${sanitizedSection}_${formattedDate}.png`;
}

/**
 * Export a card element to PNG image
 * @param element - The DOM element to export (should be in "export" mode at 1080x1080px)
 * @param filename - The filename for the downloaded image
 * @returns Promise that resolves when export completes
 */
export async function exportCardAsImage(
  element: HTMLElement,
  filename: string,
): Promise<void> {
  try {
    // Wait for fonts to load to ensure proper rendering
    await document.fonts.ready;

    // Small delay to ensure all styles are applied and animations settled
    await new Promise((resolve) => setTimeout(resolve, 100));

    // Convert element to PNG at full resolution
    const dataUrl = await toPng(element, {
      width: 1080,
      height: 1080,
      pixelRatio: 1, // Keep at 1 since we're already rendering at target size
      cacheBust: true, // Prevent caching issues
      skipFonts: true, // Skip external font stylesheets to avoid CORS errors
      style: {
        // Ensure proper rendering
        transform: "none",
        margin: "0",
        padding: "0",
      },
    });

    // Create download link and trigger download
    const link = document.createElement("a");
    link.download = filename;
    link.href = dataUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error("Failed to export card as image:", error);
    throw new Error("Failed to export card. Please try again.");
  }
}

/**
 * Export card data to image by rendering a hidden card element
 * This function creates a temporary DOM element, renders the card, exports it, and cleans up
 * @param cardData - The card data to export
 * @returns Promise that resolves when export completes
 */
export async function exportCardData(): Promise<void> {
  // Note: This function requires a React render context
  // In practice, we'll use a hidden div in the component tree with a ref
  // This is a placeholder for the full implementation
  throw new Error("Use exportCardAsImage with a rendered card element");
}

/**
 * Generate a blob from a card element (useful for sharing via Web Share API)
 * @param element - The DOM element to export
 * @returns Promise that resolves with image blob
 */
export async function exportCardAsBlob(element: HTMLElement): Promise<Blob> {
  try {
    await document.fonts.ready;
    await new Promise((resolve) => setTimeout(resolve, 100));

    const dataUrl = await toPng(element, {
      width: 1080,
      height: 1080,
      pixelRatio: 1,
      cacheBust: true,
      skipFonts: true, // Skip external font stylesheets to avoid CORS errors
    });

    // Convert data URL to blob
    const response = await fetch(dataUrl);
    const blob = await response.blob();
    return blob;
  } catch (error) {
    console.error("Failed to export card as blob:", error);
    throw new Error("Failed to generate card image.");
  }
}
